import { Alert, Grid } from '@mui/material';
import { get } from 'lodash-es';
import React, { useEffect } from 'react';
import { generatePath, useNavigate, useNavigationType, useParams } from 'react-router-dom';

import ContractSkeleton from '../components/Contracts/ContractSkeleton';
import FocusedContract from '../components/Contracts/FocusedContract';
import { ADMIN_CONTRACT_REPORTS_URL } from '../constants/routeUrls';
import useAdvertiser from '../hooks/useAdvertiser';
import { useContractConfigurationContext } from '../hooks/useContractConfigurationContext';

export enum ContractStep {
  Details,
  Configuration,
  ReviewAndConfirm,
}

// True when document.referrer is empty or from a different origin.
function isExternalReferrer(): boolean {
  if (!document.referrer) return true;
  try {
    return new URL(document.referrer).origin !== window.location.origin;
  } catch {
    return true;
  }
}

function ContractPage() {
  const contractConfiguration = useContractConfigurationContext();
  const data = get(contractConfiguration, 'contractData');
  const isLoading = get(contractConfiguration, 'isRefetching');
  const isError = get(contractConfiguration, 'isError');

  const advertiser = useAdvertiser(data?.advertiser_id);

  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const params = useParams();

  // Rdirect Spending contracts to /reports on external POP loads; PUSH preserves the Edit page for in-SPA navigation.
  useEffect(() => {
    if (!data) return;
    if (navigationType !== 'POP') return;
    if (data.derived_ui_state !== 'spending') return;
    if (!isExternalReferrer()) return;
    if (!params.id) return;
    navigate(generatePath(ADMIN_CONTRACT_REPORTS_URL, { id: String(params.id) }), { replace: true });
  }, [data, navigationType, navigate, params.id]);

  if (isLoading || (data?.advertiser_id && advertiser.isLoading)) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <ContractSkeleton />
        </Grid>
      </Grid>
    );
  }

  if (data?.code === 404) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Alert severity="error">Contract not found</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isError) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Alert severity="error">Failed to load contract</Alert>
        </Grid>
      </Grid>
    );
  }

  return <FocusedContract advertiserData={advertiser.data} activeStep={ContractStep.Details} />;
}

export default ContractPage;
